import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";

function timeAgo(iso) {
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return d.toLocaleDateString();
}

function CommentItem({ c, canDelete, onDelete }) {
  const isCoach = c.author_role === "admin";
  return (
    <div data-testid={`thread-comment-${c.comment_id}`}
         className={isCoach ? "border-l-2 border-[#C9A227] bg-[#1B3A2B]/30 p-3" : "border-l border-white/10 p-3"}>
      <div className="flex items-center gap-2 mb-1">
        <span className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-[#050A08]"
              style={{ background: c.author_avatar || "#C9A227" }}>
          {(c.author_name || "G").slice(0, 1).toUpperCase()}
        </span>
        <span className="text-xs text-white">{c.author_name}</span>
        {isCoach && <span className="overline" style={{ color: "#C9A227" }}>Coach</span>}
        <span className="text-white/30 text-[11px] ml-auto">{timeAgo(c.created_at)}</span>
        {canDelete && (
          <button onClick={() => onDelete(c.comment_id)} data-testid={`thread-comment-delete-${c.comment_id}`}
                  title="Delete comment" className="text-white/30 hover:text-red-400 text-sm leading-none ml-1">×</button>
        )}
      </div>
      <div className={`text-sm whitespace-pre-line ${isCoach ? "text-white" : "text-white/85"}`}>{c.body}</div>
    </div>
  );
}

export const CommentThread = ({ videoId, onCount }) => {
  const { user } = useAuth();
  const nav = useNavigate();
  const [comments, setComments] = useState([]);
  const [body, setBody] = useState("");
  const [err, setErr] = useState("");
  const [posting, setPosting] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/videos/${videoId}/comments`);
      setComments(data);
      if (onCount) onCount(data.length);
    } catch (e) {
      console.error("Failed to load comments:", e);
    } finally {
      setLoading(false);
    }
  }, [videoId, onCount]);

  useEffect(() => { load(); }, [load]);

  const removeComment = async (cid) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/comments/${cid}`);
      setComments((c) => c.filter((x) => x.comment_id !== cid));
    } catch (e) {
      console.error("Failed to delete comment:", e);
    }
  };

  const canDelete = (c) => user && (user.role === "admin" || user.id === c.user_id);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { nav("/login"); return; }
    if (!body.trim()) return;
    setPosting(true); setErr("");
    try {
      const { data } = await api.post(`/videos/${videoId}/comments`, { body });
      setComments((c) => [...c, data]);
      setBody("");
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setPosting(false);
    }
  };

  return (
    <div data-testid={`comment-thread-${videoId}`} className="bg-[#08100D] border border-white/5 p-4">
      <div className="overline">Coaching thread</div>
      <div className="mt-3 space-y-3 max-h-[300px] overflow-auto pr-1">
        {loading ? (
          <p className="text-white/40 text-sm">Loading notes…</p>
        ) : comments.length === 0 ? (
          <p className="text-white/40 text-sm">No coaching notes yet. Ask the coach a question.</p>
        ) : comments.map((c) => <CommentItem key={c.comment_id} c={c} canDelete={canDelete(c)} onDelete={removeComment} />)}
      </div>

      {user ? (
        <form onSubmit={submit} className="mt-3 space-y-2">
          <textarea data-testid={`thread-input-${videoId}`} rows={2} value={body} onChange={(e) => setBody(e.target.value)}
                    className="input-dark !py-2 text-sm" placeholder={user.role === "admin" ? "Reply as coach…" : "Ask the coach a question…"} />
          {err && <div className="text-red-400 text-xs">{err}</div>}
          <button data-testid={`thread-submit-${videoId}`} disabled={posting || !body.trim()} className="btn-primary !py-2 !px-5 text-xs">
            {posting ? "Posting…" : user.role === "admin" ? "Reply" : "Post"}
          </button>
        </form>
      ) : null}
    </div>
  );
};
